import { CURRENCY_CODE } from "../billing/money.js";
import { idCodec } from "../id/brand.js";
import { accountId as accountIdCodec, workspaceId as workspaceIdCodec } from "../id/codec.js";
import { MENU_SAMPLE_MAXIMUM } from "./delivery/menu-count.js";
/** The id codec of a document preview (UUID v7, minted in the application). */
export const documentPreviewIdCodec = idCodec("DocumentPreviewId");
/** `document_preview.state` (migration 0128's CHECK). */
export const DOCUMENT_PREVIEW_STATE = ["uploading", "queued", "running", "done", "failed"];
/**
 * The one sentence a preview that could not be read shows (design §28.5.12). Plain, and never blocking: the person can
 * still build, and the build reads the menu itself.
 */
export const DOCUMENT_PREVIEW_UNREADABLE_MESSAGE = "We couldn't read the menu from this file. You can still build.";
/** The outbox topic of a preview's extraction (a FACTORY topic). */
export const DOCUMENT_PREVIEW_EXTRACT_TOPIC = "document_preview.extract";
/** Thrown for a `document_preview.extract` message whose payload is not the preview's id. A producer defect. */
export class DocumentPreviewExtractPayloadError extends Error {
    name = "DocumentPreviewExtractPayloadError";
}
/** The JSON written to `outbox_message.payload` (the account and workspace are the row's own columns). */
export function documentPreviewExtractWireOf(payload) {
    return { document_preview_id: payload.documentPreviewId };
}
/** Reads a `document_preview.extract` message back — refusing anything but the id, and a message with no workspace. */
export function documentPreviewExtractPayloadOf(message) {
    const payload = message.payload;
    if (typeof payload !== "object" || payload === null || Array.isArray(payload)) {
        throw new DocumentPreviewExtractPayloadError(`document_preview.extract ${message.outboxMessageId} has no payload object.`);
    }
    if (message.workspaceId === null) {
        throw new DocumentPreviewExtractPayloadError(`document_preview.extract ${message.outboxMessageId} names no workspace.`);
    }
    if (!accountIdCodec.is(message.accountId) || !workspaceIdCodec.is(message.workspaceId)) {
        throw new DocumentPreviewExtractPayloadError(`document_preview.extract ${message.outboxMessageId} names a malformed account or workspace.`);
    }
    const raw = payload["document_preview_id"];
    if (typeof raw !== "string" || !documentPreviewIdCodec.is(raw)) {
        throw new DocumentPreviewExtractPayloadError(`document_preview.extract ${message.outboxMessageId} has no valid document_preview_id.`);
    }
    return {
        kind: DOCUMENT_PREVIEW_EXTRACT_TOPIC,
        accountId: message.accountId,
        workspaceId: message.workspaceId,
        documentPreviewId: raw,
    };
}
/** The sample, as the factory writes it into `document_preview.sample`. */
export function documentPreviewSampleWireOf(sample) {
    return sample.slice(0, MENU_SAMPLE_MAXIMUM).map((one) => ({
        name: one.name,
        price_minor: one.priceMinor,
        currency: one.currency,
        size_price: one.sizePrice.map((each) => ({ size: each.size, price_minor: each.priceMinor })),
    }));
}
function isMinor(value) {
    return typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
}
/**
 * Reads a stored sample back, item by item. A row the factory wrote always reads; anything else (a hand-edited row)
 * is `null`, so a reader serves "no sample" rather than a shape it cannot vouch for.
 */
export function documentPreviewSampleOf(value) {
    if (!Array.isArray(value) || value.length > MENU_SAMPLE_MAXIMUM)
        return null;
    const read = [];
    for (const one of value) {
        if (typeof one !== "object" || one === null || Array.isArray(one))
            return null;
        const shaped = one;
        const name = shaped["name"];
        const priceMinor = shaped["price_minor"];
        const currency = shaped["currency"];
        const sized = shaped["size_price"];
        if (typeof name !== "string" || !isMinor(priceMinor) || currency !== CURRENCY_CODE || !Array.isArray(sized))
            return null;
        const sizePrice = [];
        for (const each of sized) {
            if (typeof each !== "object" || each === null || Array.isArray(each))
                return null;
            const size = each["size"];
            const price = each["price_minor"];
            if (typeof size !== "string" || !isMinor(price))
                return null;
            sizePrice.push({ size, priceMinor: price });
        }
        read.push({ name, priceMinor, currency: CURRENCY_CODE, sizePrice });
    }
    return read;
}
//# sourceMappingURL=document-preview.js.map